import { useState } from 'react'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { PageHeader } from '@/app/layouts/PageHeader'
import { BRAND } from '@/brand/brand'
import { cn } from '@/lib/cn'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from './SettingsScreen.module.css'

interface Toggle {
  readonly id: string
  readonly icon: string
  readonly title: string
  readonly body: string
  readonly on: boolean
}

const ALERTS: readonly Toggle[] = [
  { id: 'in', icon: 'south_west', title: 'Money in', body: 'Push and email when anything lands', on: true },
  { id: 'large', icon: 'priority_high', title: 'Large payments', body: 'Anything over $1,000', on: true },
  { id: 'corridor', icon: 'send', title: 'Transfer delivered', body: 'When money reaches the recipient', on: true },
  { id: 'rate', icon: 'trending_up', title: 'Rate alerts', body: 'When your corridor moves more than 1%', on: false },
  { id: 'marketing', icon: 'campaign', title: 'News and offers', body: 'At most one a month', on: false },
]

const LANGUAGES = ['English', 'Español', 'Português'] as const

export function SettingsScreen() {
  const [toggles, setToggles] = useState<readonly Toggle[]>(ALERTS)
  const [language, setLanguage] = useState<string>('English')

  const flip = (id: string) =>
    setToggles((current) =>
      current.map((item) => (item.id === id ? { ...item, on: !item.on } : item)),
    )

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="How the app behaves and what it tells you about."
        actions={
          <Button variant="ghost" size="md" iconStart="shield" to="/profile">
            Profile & security
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              What we tell you about
            </Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {toggles.map((item) => (
                <div key={item.id} className={checkout.reason}>
                  <Icon name={item.icon} size={20} />
                  <Stack grow gap={1}>
                    <Text variant="caption" weight="semibold">
                      {item.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {item.body}
                    </Text>
                  </Stack>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={item.on}
                    aria-label={item.title}
                    className={cn(styles.switch, item.on && styles.switchOn)}
                    onClick={() => flip(item.id)}
                  >
                    <span className={styles.knob} />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Stack gap={3}>
            <Card tone="light">
              <Text variant="caption" weight="semibold">
                Language
              </Text>
              <Text variant="eyebrow" tone="secondary" className="pb-mt-2">
                Support answers in the language you pick here
              </Text>
              <Stack direction="row" gap={2} wrap className="pb-mt-4">
                {LANGUAGES.map((item) => (
                  <Button
                    key={item}
                    variant={language === item ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setLanguage(item)}
                  >
                    {item}
                  </Button>
                ))}
              </Stack>
            </Card>

            <Card tone="light">
              <Text variant="caption" weight="semibold">
                About
              </Text>
              <Stack gap={0} className="pb-mt-2">
                {[
                  ['App', `${BRAND.name} for web`],
                  ['Version', '0.1.0'],
                  ['Region', 'United States'],
                ].map(([label, value]) => (
                  <div key={label} className={checkout.line}>
                    <Text variant="caption" tone="secondary">
                      {label}
                    </Text>
                    <Text variant="caption" weight="semibold">
                      {value}
                    </Text>
                  </div>
                ))}
              </Stack>
              <Card.Footer>
                <Stack direction="row" gap={2} wrap>
                  <Button variant="outline" size="sm" to="/legal/terms">
                    Terms
                  </Button>
                  <Button variant="outline" size="sm" to="/legal/photography">
                    Photography
                  </Button>
                  <Button variant="outline" size="sm" to="/legal/privacy">
                    Privacy
                  </Button>
                  <Button variant="outline" size="sm" to="/support">
                    Support
                  </Button>
                </Stack>
              </Card.Footer>
            </Card>
          </Stack>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="dark">
            <Stack direction="row" align="center" gap={3} wrap>
              <Icon name="download" size={20} />
              <Stack grow gap={0}>
                <Text variant="caption" weight="semibold" tone="onDark">
                  Take your data with you
                </Text>
                <Text variant="eyebrow" tone="onDarkMuted">
                  Everything we hold, as CSV and PDF, sent to your email
                </Text>
              </Stack>
              <Pill tone="onDark">Ready in a day</Pill>
              <Button variant="accentDim" size="sm">
                Request export
              </Button>
            </Stack>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
